import { t } from "@travily/i18n";
import type ImageType from "@travily/types/shared/Image";
import { formatNativePrice, formatPrice, isDisplayConverted } from "@ui/lib/format-price";
import type { ShowViewProps } from "@/components/ProductShowView/ShowView";

// i18n-tasks-use t('web.products.show.price_free')
// i18n-tasks-use t('web.products.show.price_paid')
// i18n-tasks-use t('web.products.show.price_note_approximate')
// i18n-tasks-use t('web.services.show.price_free')
// i18n-tasks-use t('web.services.show.price_paid')
// i18n-tasks-use t('web.services.show.price_note_approximate')

/** The display-only subset of `ShowView`'s props. The interactive props
 *  (`onPurchase`, `onShare`, `onFavoriteToggle`, `onAddReview`, `downloadHref`,
 *  `manageHref`, `favorited`, `shared`, `reviewsListWrapper`) are owned by the
 *  caller because they depend on page-level hooks and state. */
export type ShowViewDisplayProps = Pick<
  ShowViewProps,
  | "averageScore"
  | "creator"
  | "description"
  | "tagline"
  | "images"
  | "priceLabel"
  | "priceNote"
  | "reviewCount"
  | "reviews"
  | "title"
>;

/** The product/service listing fields the buyer view reads. Both `ProductsShow`
 *  ["product"] and `ServicesShow`["service"] satisfy this structurally. */
interface ListingShowData {
  title: string | null;
  tagline: string | null;
  description: string | null;
  average_score: number | null;
  reviews_count: number;
  free: boolean;
  price_cents: number | null;
  price_currency: string | null;
  photos: Array<{ image: ImageType | null }>;
}

interface ReviewShowData {
  id: number;
  score: number;
  body: string;
  author_name: string | null;
  author_initial: string | null;
  author_profile_type: string | null;
  author_avatar: ImageType | null;
}

interface ListingPriceData {
  free: boolean;
  price_cents: number | null;
  price_currency: string | null;
}

/** The buyer-facing price label ("Get for free" / "Buy for £20.00"), in the
 *  display currency. Shared by the show pages and the publish-stage summary card. */
export function listingPriceLabel(kind: "product" | "service", listing: ListingPriceData): string {
  const ns = `web.${kind}s.show` as const;
  return listing.free || listing.price_cents == null || listing.price_currency == null
    ? t(`${ns}.price_free`)
    : t(`${ns}.price_paid`, { price: formatPrice(listing.price_cents, listing.price_currency) });
}

/**
 * Maps the Inertia show-page payload into the buyer `ShowView`'s display props.
 * Shared by the live product/service pages and their read-only creator previews
 * so the preview is guaranteed pixel-identical to what buyers actually see.
 */
export function buildShowViewProps(
  kind: "product" | "service",
  listing: ListingShowData,
  creator: ShowViewProps["creator"],
  reviews: ReviewShowData[],
): ShowViewDisplayProps {
  const ns = `web.${kind}s.show` as const;

  const priceLabel = listingPriceLabel(kind, listing);

  const priceNote =
    !listing.free &&
    listing.price_cents != null &&
    listing.price_currency != null &&
    isDisplayConverted(listing.price_currency)
      ? t(`${ns}.price_note_approximate`, { price: formatNativePrice(listing.price_cents, listing.price_currency) })
      : undefined;

  return {
    averageScore: listing.average_score,
    creator,
    description: listing.description,
    tagline: listing.tagline,
    images: listing.photos.flatMap((photo, i) =>
      photo.image ? [{ ...photo.image, alt: photo.image.alt || t("common.photo_alt", { number: i + 1 }) }] : [],
    ),
    priceLabel,
    priceNote,
    reviewCount: listing.reviews_count,
    reviews: reviews.map((review) => ({
      id: review.id,
      author: {
        profile_type: review.author_profile_type ?? "Family",
        avatar: review.author_avatar,
        initial: review.author_initial ?? "",
        name: review.author_name ?? "",
      },
      body: review.body,
      rating: review.score,
    })),
    title: listing.title ?? "",
  };
}
